import type { Tool } from '@ag-ui/client';
import { z } from 'zod';

import type { ChatEntityCardType } from '../chat-slot-renderers';
import {
    generativeUiComponents as components,
    type GenerativeUiComponentType,
    NAVIGATE_ACTION,
    navigateParamsSchema,
    SEND_MESSAGE_ACTION,
    sendMessageParamsSchema,
} from './catalog';

export const RENDER_UI_TOOL_NAME = 'render_ui';

const LAYOUTS = (Object.keys(components) as GenerativeUiComponentType[]).filter(
    (type) => type !== 'EntityCard' && components[type].hasChildren,
);

const parentsOf = (entityCards: readonly ChatEntityCardType[]): string =>
    [...LAYOUTS, ...(entityCards.length === 0 ? [] : ['EntityCard'])].join(', ');

const elementShape = <TType extends GenerativeUiComponentType, TProps extends z.ZodType>(
    type: TType,
    props: TProps,
) => ({
    id: z.string().describe('Unique id of this element, referenced by root and children.'),
    type: z.literal(type),
    props,
    children: z
        .array(z.string())
        .describe(
            `Ids of the child elements; empty unless the type is a layout (${LAYOUTS.join(', ')}) or an EntityCard.`,
        ),
});

const element = <TType extends Exclude<GenerativeUiComponentType, 'Button' | 'EntityCard'>, TProps extends z.ZodObject>(
    type: TType,
    props: TProps,
) => z.strictObject(elementShape(type, props)).describe(components[type].description);

const buttonElementSchema = z
    .strictObject({
        ...elementShape('Button', components.Button.props),
        on: z.strictObject({
            press: z.union([
                z.strictObject({ action: z.literal(NAVIGATE_ACTION), params: navigateParamsSchema }),
                z.strictObject({ action: z.literal(SEND_MESSAGE_ACTION), params: sendMessageParamsSchema }),
            ]),
        }),
    })
    .describe(components.Button.description);

const fixedElementSchemas = [
    element('Stack', components.Stack.props),
    element('Row', components.Row.props),
    element('Grid', components.Grid.props),
    element('Card', components.Card.props),
    element('Tabs', components.Tabs.props),
    element('Collapsible', components.Collapsible.props),
    element('Text', components.Text.props),
    element('Metric', components.Metric.props),
    element('Badge', components.Badge.props),
    element('Progress', components.Progress.props),
    element('Table', components.Table.props),
    element('Timeline', components.Timeline.props),
    element('Callout', components.Callout.props),
    element('KeyValue', components.KeyValue.props),
    element('BarChart', components.BarChart.props),
    element('LineChart', components.LineChart.props),
    element('EmptyState', components.EmptyState.props),
    element('Choice', components.Choice.props),
    element('ChangeReceipt', components.ChangeReceipt.props),
    buttonElementSchema,
] as const;

const argumentsSchema = <TElement extends z.ZodType>(elementSchema: TElement) =>
    z.strictObject({
        root: z.string().describe('Id of the element the card starts with, usually a Stack or a Card.'),
        elements: z.array(elementSchema),
    });

/**
 * Reads `render_ui` arguments: a flat element list instead of json-render's element map, so the tool
 * schema stays valid for OpenAI strict mode (closed objects, every property required, no maps).
 * Entity cards parse with any entity and ids; only the feature that knows the entity can tell them apart.
 */
export const renderUiArgumentsSchema = argumentsSchema(
    z.union([...fixedElementSchemas, z.strictObject(elementShape('EntityCard', components.EntityCard.props))]),
);

type RenderUiArguments = z.infer<typeof renderUiArgumentsSchema>;
export type RenderUiElement = RenderUiArguments['elements'][number];

const entityCardElementSchema = ({ description, idNames, type }: ChatEntityCardType) =>
    z
        .strictObject(
            elementShape(
                'EntityCard',
                z.strictObject({
                    entity: z.literal(type),
                    ids: z.strictObject(Object.fromEntries(idNames.map((name) => [name, z.string()]))),
                }),
            ),
        )
        .describe(`${components.EntityCard.description} ${type}: ${description}`);

const toToolParameters = (entityCards: readonly ChatEntityCardType[]): Record<string, unknown> => {
    const schema = argumentsSchema(z.union([...fixedElementSchemas, ...entityCards.map(entityCardElementSchema)]));
    const { $schema: _dialect, ...parameters } = z.toJSONSchema(schema);
    return parameters;
};

const entityCardLines = (entityCards: readonly ChatEntityCardType[]): string[] =>
    entityCards.length === 0
        ? []
        : [
              `- EntityCard {entity, ids}: ${components.EntityCard.description} When your reply lists, counts, compares or summarises app items that have a card, call render_ui with those cards instead of writing a markdown table or bullet list of them; keep your findings as text. Entities:`,
              ...entityCards.map(
                  ({ description, idNames, type }) => `  - ${type} {${idNames.join(', ')}}: ${description}`,
              ),
          ];

const description = (entityCards: readonly ChatEntityCardType[]): string =>
    [
        'Show interactive UI in your reply, built only from the components below.',
        'Use it when structure helps more than prose: key figures, statuses, comparisons, plans, an overview with next steps, real app items as live cards, or choices the user can pick with one click or from radio buttons.',
        'Do not use it for long prose or anything the user has to type or edit; answer in text then.',
        'Do not repeat the UI content in your text reply; refer to it instead.',
        'Arguments: root is the id of the first element. elements is a flat list; every element has a unique id, a type, its props and children (the ids of its child elements, each used once).',
        `Only ${parentsOf(entityCards)} have children; every other element has children: [].`,
        'Composition: a Card or Stack root; a Grid of Metrics for key figures; Table or BarChart to compare, LineChart for a trend, Timeline for a plan, a ChangeReceipt per change you made, a Callout for the one thing to act on, a Row of Buttons for next steps. Usually 3 to 15 elements.',
        'When you finish work a card from an earlier reply tracks, update only its steps there with update_ui, and show the result, such as the new item as a live card and its next-step Buttons, with a new render_ui at the end of this reply.',
        'Components:',
        `- Stack {gap: sm|md|lg}: ${components.Stack.description}`,
        `- Row {gap, align: start|center|between}: ${components.Row.description}`,
        `- Grid {columns: 2|3|4}: ${components.Grid.description}`,
        `- Card {title, subtitle}: ${components.Card.description}`,
        `- Tabs {labels}: ${components.Tabs.description}`,
        `- Collapsible {title, isOpen}: ${components.Collapsible.description}`,
        `- Text {text, variant: heading|body|caption}: ${components.Text.description}`,
        `- Metric {label, value, hint}: ${components.Metric.description}`,
        `- Badge {label, tone: gray|info|success|warning|error}: ${components.Badge.description}`,
        `- Progress {label, value, max}: ${components.Progress.description}`,
        `- Table {columns, rows}: ${components.Table.description}`,
        `- Timeline {items: [{title, subtitle, status: null|pending|running|done|failed|skipped}]}: ${components.Timeline.description}`,
        `- Callout {tone: info|success|warning|error, title, text}: ${components.Callout.description}`,
        `- KeyValue {items: [{label, value}]}: ${components.KeyValue.description}`,
        `- BarChart {items: [{label, value, hint}], unit}: ${components.BarChart.description}`,
        `- LineChart {title, points: [{label, value}], unit}: ${components.LineChart.description}`,
        `- EmptyState {text}: ${components.EmptyState.description}`,
        `- Choice {question, options: [{label, description}], multiple, recommended, submitLabel}: ${components.Choice.description}`,
        `- ChangeReceipt {verb, caption, title, detail}: ${components.ChangeReceipt.description}`,
        ...entityCardLines(entityCards),
        `- Button {label, variant: primary|secondary|tertiary}: ${components.Button.description} Set on: {press: {action: "${NAVIGATE_ACTION}", params: {path}}} with an in-app path such as "/skills" (a path may carry a query such as "?tile=…"; never a URL), or on: {press: {action: "${SEND_MESSAGE_ACTION}", params: {text}}} with the message the user sends to you by clicking, e.g. "Show the skill gaps of Backend Developer".`,
        'Text props may mention app items as ⟦type:id|label⟧, shown as chips.',
        'Write labels and text in the language of the conversation.',
    ].join('\n');

/** The tool every AG-UI run declares so the agent can answer with a card the chat renders. */
export const createRenderUiTool = (entityCards: readonly ChatEntityCardType[]): Tool => ({
    description: description(entityCards),
    name: RENDER_UI_TOOL_NAME,
    parameters: toToolParameters(entityCards),
});
